"use client";

import Image from "next/image";
import { useDict } from "@/components/i18n/DictProvider";
import { Container } from "@/components/ui/Container";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/components/ui/cn";
import { type Lang } from "@/lib/i18n";
import { detailCrumbs } from "@/lib/routes/detailCrumbs";
import { formatDate, formatDuration, formatEpisodes } from "@/lib/format";
import { moduleBanner, moduleBannerAlt } from "@/lib/lecturehub/poster";
import { ottIsSeries, ottTotalDuration } from "./ottMeta";
import type { ModuleDetail } from "@/lib/lecturehub/types";

/**
 * SLIM branded hero for the OTT (Videoteka) watch page.
 *
 * A title band, not a full cover: badges + title + meta, so it never duplicates
 * the player that sits right below (the reason it isn't the tall DetailHeroBanner
 * the other detail types use). When the module has a hero image it becomes the
 * band's BACKGROUND — a photographic backdrop behind a legibility scrim, not a
 * separate poster — and the band grows to give the image room; without one it
 * falls back to the teal signature gradient. Only the hero image, never the
 * poster: a poster carries its own headline, which would collide with the
 * title (see moduleBanner). The player stays the primary media.
 *
 * The breadcrumb renders on a light strip below the band, as the other heroes do.
 */
export function OttHero({
  lang,
  module: m,
  library = false,
  overlayHeader = false,
  cfHash = null,
}: {
  lang: Lang;
  module: ModuleDetail;
  /** Render the in-app (/library) breadcrumb trail instead of the public one. */
  library?: boolean;
  /** Guest/public surface: the marketing header is `fixed` and transparent over
   *  the band, so add top clearance. False on the in-app (AppShell) surface. */
  overlayHeader?: boolean;
  /** Portal.cloudflareAccountHash — resolves the hero image to a CF variant. */
  cfHash?: string | null;
}) {
  const t = useDict();
  const ott = m.ott;
  const isSeries = ottIsSeries(ott);
  const totalDuration = ottTotalDuration(ott);
  const episodeCount = ott?.videos?.length ?? 0;
  const heroUrl = moduleBanner(m, cfHash);

  return (
    <>
      <section
        // Same flag DetailHeroBanner sets: the marketing header reads it and
        // goes fixed + transparent over this dark band (white nav on teal),
        // frosting once scrolled. Keeps OTT consistent with the other detail
        // heroes and makes the top padding the header's space, not a gap.
        data-header-overlay={overlayHeader ? "" : undefined}
        className="relative isolate w-full overflow-hidden bg-gradient-to-br from-teal to-foreground text-white"
      >
        {heroUrl ? (
          <>
            <Image
              src={heroUrl}
              alt={moduleBannerAlt(m, m.name)}
              fill
              priority
              sizes="100vw"
              className="object-cover"
            />
            {/* Legibility scrim so white title/meta read on any photo. */}
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/25"
            />
          </>
        ) : (
          // No photo: a faint accent bloom so the band has depth, not a flat
          // two-stop fill. Token-driven (accent), so it re-themes per portal.
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(60% 90% at 88% 0%, color-mix(in srgb, var(--color-accent) 18%, transparent), transparent 62%), radial-gradient(40% 70% at 0% 100%, rgb(255 255 255 / 0.05), transparent 60%)",
            }}
          />
        )}
        <Container
          className={cn(
            "relative flex flex-col gap-2.5 pb-7",
            // With a photo backdrop the band grows and content sits at the bottom;
            // without one it stays a compact title bar.
            heroUrl ? "justify-end min-h-[260px] md:min-h-[340px]" : "",
            // Clear the fixed, transparent marketing header (guest/public only).
            overlayHeader ? "pt-[calc(var(--marketing-header-h)+1.25rem)]" : "pt-7",
          )}
        >
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="gold">{isSeries ? t.detail.typeSeries : t.detail.typeVideo}</Badge>
            {ott?.type?.name ? (
              <span className="inline-flex items-center rounded-pill bg-white/15 px-2.5 py-0.5 font-display text-[11px] font-semibold uppercase tracking-[0.1em] text-white ring-1 ring-inset ring-white/20 backdrop-blur-sm">
                {ott.type.name}
              </span>
            ) : null}
          </div>

          <h1 className="max-w-3xl text-balance !text-[24px] !font-bold !leading-[1.12] !tracking-tight !text-white md:!text-[32px]">
            {m.name}
          </h1>

          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px] tabular-nums text-white/80">
            {isSeries && episodeCount > 0 ? (
              <>
                <span>{formatEpisodes(episodeCount, lang, t.units)}</span>
                {totalDuration > 0 ? <Dot /> : null}
              </>
            ) : null}
            {totalDuration > 0 ? <span>{formatDuration(totalDuration)}</span> : null}
            {m.createdAt ? (
              <>
                {totalDuration > 0 || (isSeries && episodeCount > 0) ? <Dot /> : null}
                <span>{formatDate(m.createdAt, lang)}</span>
              </>
            ) : null}
          </div>
        </Container>
      </section>

      <section>
        {/* Balanced padding: equal space above and below the breadcrumb, so it
            doesn't hug the muted stage band that follows. */}
        <Container className="py-5">
          <Breadcrumb items={detailCrumbs(t, lang, "otts", m.name, library)} />
        </Container>
      </section>
    </>
  );
}

function Dot() {
  return <span aria-hidden className="inline-block size-[3px] rounded-full bg-white/45" />;
}
